import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useModulos } from "@/hooks/use-modulos";

interface Solicitacao {
  id: string;
  parceiroId: string;
  vistoriadoraId: string;
  solicitadoPor: string;
  mes: number;
  ano: number;
  status: "pendente" | "aprovada" | "rejeitada" | "utilizada";
  aprovadoPor: string | null;
  laudoId: string | null;
  motivoRejeicao: string | null;
  criadoEm: string;
  parceiroNome?: string;
  solicitadoPorNome?: string;
}

const STATUS_CONFIG = {
  pendente: {
    label: "Pendente",
    icon: Clock,
    className: "bg-amber-100 text-amber-700",
  },
  aprovada: {
    label: "Aprovada",
    icon: CheckCircle2,
    className: "bg-emerald-100 text-emerald-700",
  },
  rejeitada: {
    label: "Rejeitada",
    icon: XCircle,
    className: "bg-red-100 text-red-700",
  },
  utilizada: {
    label: "Utilizada",
    icon: CheckCircle2,
    className: "bg-slate-100 text-slate-500",
  },
};

const MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Solicitacoes() {
  const { profile, session, loading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { modulos } = useModulos();

  const isAdminRt =
    profile?.roles?.some((r) => ["admin", "rt", "owner"].includes(r)) ?? false;
  const isParceiro = profile?.roles?.includes("parceiro_negocios") ?? false;

  const [statusFiltro, setStatusFiltro] = useState<string>("pendente");
  const [rejeicaoDialog, setRejeicaoDialog] = useState<{ id: string } | null>(
    null,
  );
  const [motivo, setMotivo] = useState("");

  const {
    data: solicitacoes,
    isLoading,
    refetch,
  } = useQuery<Solicitacao[]>({
    queryKey: ["/api/solicitacoes-laudo", statusFiltro],
    queryFn: async () => {
      const res = await fetch(
        `/api/solicitacoes-laudo?status=${statusFiltro}`,
        {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        },
      );
      if (!res.ok) throw new Error("Erro ao carregar");
      return res.json();
    },
    enabled: !!session && (isAdminRt || isParceiro),
  });

  const aprovarMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/solicitacoes-laudo/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ status: "aprovada" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao aprovar");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/solicitacoes-laudo"] });
      toast({ title: "Solicitação aprovada com sucesso" });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  const rejeitarMutation = useMutation({
    mutationFn: async ({
      id,
      motivoRejeicao,
    }: {
      id: string;
      motivoRejeicao: string;
    }) => {
      const res = await fetch(`/api/solicitacoes-laudo/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ status: "rejeitada", motivoRejeicao }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao rejeitar");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/solicitacoes-laudo"] });
      setRejeicaoDialog(null);
      setMotivo("");
      toast({ title: "Solicitação rejeitada" });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!isAdminRt && !isParceiro) {
    navigate("/");
    return null;
  }

  if (modulos.laudosGratisPorCliente == 0) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-display text-slate-900">
                Solicitações de Laudo
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                Parceiros que atingiram o limite de laudos/associado e precisam
                de autorização
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros de status */}
      <div className="flex flex-wrap gap-2 mb-6">
        {["pendente", "aprovada", "rejeitada", "utilizada"].map((s) => {
          const cfg = STATUS_CONFIG[s as keyof typeof STATUS_CONFIG];
          const Icon = cfg.icon;
          return (
            <button
              key={s}
              onClick={() => setStatusFiltro(s)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all",
                statusFiltro === s
                  ? cn(cfg.className, "border-transparent")
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-300",
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {cfg.label}
            </button>
          );
        })}
      </div>

      <Card className="border-none shadow-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-900">
            Solicitações{" "}
            {STATUS_CONFIG[
              statusFiltro as keyof typeof STATUS_CONFIG
            ]?.label.toLowerCase()}
            s ({solicitacoes?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array(4)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded" />
                ))}
            </div>
          ) : !solicitacoes || solicitacoes.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <ClipboardList className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">
                Nenhuma solicitação{" "}
                {STATUS_CONFIG[
                  statusFiltro as keyof typeof STATUS_CONFIG
                ]?.label.toLowerCase()}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Data</TableHead>
                  <TableHead className="text-xs">Parceiro</TableHead>
                  <TableHead className="text-xs">Solicitante</TableHead>
                  <TableHead className="text-xs">Período</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  {isAdminRt && statusFiltro === "pendente" && (
                    <TableHead className="text-xs text-right">Ações</TableHead>
                  )}
                  {statusFiltro === "rejeitada" && (
                    <TableHead className="text-xs">Motivo</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {solicitacoes.map((sol) => {
                  const cfg = STATUS_CONFIG[sol.status];
                  const Icon = cfg.icon;
                  return (
                    <TableRow key={sol.id} className="text-sm">
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {fmtDate(sol.criadoEm)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {sol.parceiroNome ?? sol.parceiroId.slice(0, 8)}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {sol.solicitadoPorNome ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {MESES[(sol.mes ?? 1) - 1]}/{sol.ano}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "gap-1 text-xs font-medium border-0",
                            cfg.className,
                          )}
                        >
                          <Icon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                      </TableCell>
                      {isAdminRt && statusFiltro === "pendente" && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => setRejeicaoDialog({ id: sol.id })}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Rejeitar
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => aprovarMutation.mutate(sol.id)}
                              disabled={aprovarMutation.isPending}
                            >
                              {aprovarMutation.isPending ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                              )}
                              Aprovar
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      {statusFiltro === "rejeitada" && (
                        <TableCell className="text-xs text-slate-500">
                          {sol.motivoRejeicao ?? "—"}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de rejeição */}
      <Dialog
        open={!!rejeicaoDialog}
        onOpenChange={(o) => !o && setRejeicaoDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Solicitação</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-600 mb-3">
              Informe o motivo da rejeição. O parceiro poderá visualizá-lo.
            </p>
            <Textarea
              placeholder="Ex: Parceiro não possui créditos suficientes para este período."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejeicaoDialog(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!motivo.trim() || rejeitarMutation.isPending}
              onClick={() =>
                rejeicaoDialog &&
                rejeitarMutation.mutate({
                  id: rejeicaoDialog.id,
                  motivoRejeicao: motivo.trim(),
                })
              }
            >
              {rejeitarMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
